"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";

/**
 * A credited name that links to its page even when the credit only stores the
 * name (Gordon 2026-11-02: "make sure you can click on credits names and they
 * are linked to their pages") — the artist page by that name, else the member
 * with that name. A Label/Publisher credit opens the label's page first
 * (Gordon 2026-11-03: "Label: Breakin Bread takes me to the search").
 * Looked up once per name per visit.
 */

const COMPANY_ROLE = /label|publish|distribut|imprint|recorded at|mixed at|mastered at/i;
const cache = new Map<string, Promise<string>>();

export function creditHref(name: string, role?: string): Promise<string> {
  const n = name.trim();
  const company = !!role && COMPANY_ROLE.test(role);
  const key = `${company ? "label:" : ""}${n.toLowerCase()}`;
  if (!n) return Promise.resolve("");
  let hit = cache.get(key);
  if (!hit) {
    hit = (async () => {
      try {
        if (company) {
          // The label page opens even when the label is hidden from the Labels
          // list — but not when the label is private (not public at all).
          const l = await getDocs(query(collection(db, "labels"), where("name", "==", n), limit(1)));
          const ld = l.docs[0]?.data() as any;
          if (ld?.privacy === "private") return "";
          if (l.docs[0]) return `/labels/${ld.slug || l.docs[0].id}`;
        }
        const a = await getDocs(query(collection(db, "artists"), where("name", "==", n), limit(1)));
        const ad = a.docs[0];
        if (ad && !(ad.data() as any).adminHidden) return `/artists/${(ad.data() as any).slug || ad.id}`;
        const u = await getDocs(query(collection(db, "users"), where("displayName", "==", n), limit(1)));
        if (u.docs[0]) return `/profile/${u.docs[0].id}`;
      } catch { /* not readable — plain name */ }
      return "";
    })();
    cache.set(key, hit);
  }
  return hit;
}

export function CreditLink({ name, href, role, className }: { name: string; href?: string; role?: string; className?: string }) {
  const [found, setFound] = useState(href || "");
  useEffect(() => {
    if (href) { setFound(href); return; }
    let alive = true;
    // No page yet → their credits in search, so every name opens something.
    void creditHref(name, role).then(h => { if (alive) setFound(h || `/search?q=${encodeURIComponent(name)}`); });
    return () => { alive = false; };
  }, [name, href, role]);
  return found
    ? <Link href={found} className={className ?? "text-primary hover:underline"}>{name}</Link>
    : <span>{name}</span>;
}
